import { c as _c } from "react/compiler-runtime";
import { Component, lazy, Suspense, useEffect } from 'react';
import { StyleProvider } from '@ant-design/cssinjs';
import { withProfiler } from '@sentry/react';
import type { Persistor } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import { Provider, connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Router, Switch } from 'react-router-dom';
import { Alert, PageLoader, ErrorBoundary, GenericError } from '@commutifi-fe/ui';
import { DEFAULT_LOCALE } from 'constants/settings';
import history from './browserHistory';
import { type RootState, type Store } from './store/index';
import { isRecoverableLoadError, handleChunkLoadError } from 'utils/chunkErrorRecovery';
import { CookiesProvider } from 'react-cookie';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { MetricsLocalizer } from '@commutifi-fe/commutifi-specific/Presentation/MetricsLocalizer';
import { ConfigProvider, OnlineStatusProvider, useConfig, useCurrentAccount, useOnlineStatus, AppThemes } from 'shared/providers';
import { CurrentAccountProvider } from 'shared/providers/CurrentAccount/CurrentAccountProvider';
import CookieConsent from './CookieConsent';
import { authSelectors } from 'store/modules/auth';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { AdminDashboardPages, AdminRouting, CommonRouting, CommuterRouting, PublicRouting } from './routing';
import { FormattedMessage } from './locales/index';
import { getAuthenticatedAccountId, setLocaleHeader } from './api';
import * as qs from 'utils/queryParams';
import { LocalesType, uuidPattern } from '@commutifi-fe/constants';
import { needsOnboardingInfo } from 'utils/helpers/accounts';
import { getUrlSubdomain } from 'utils/domains';
import { useSsoLandingHydration } from 'shared/hooks/useSsoLandingHydration';
import { useSubdomain } from 'api/modules/subdomains';
import { queryClient, queryCache, useReactQueryOnError } from 'api/reactQuery';
import { logger } from '@commutifi-fe/logger';
import { DashboardErrorFallback, handleDashboardError } from 'shared/components/common/DashboardErrorFallback';
import { SuperAdminAlert } from './SuperAdminAlert';
import DevTools from './store/DevTools';
const SurveyLandingPage = lazy(() => import('views/Anonymous/views/Survey'));
const BusinessDashboardProviders = lazy(() => import('views/SignedIn/BusinessDashboard'));
const AnonymousLandingPage = lazy(() => import('views/Anonymous/views/Survey/SurveyLandingPage/AnonymousLandingPage'));
const EnterpriseRegistration = lazy(() => import('views/Anonymous/views/EnterpriseRegistration'));
const UserDashboard = lazy(() => import('views/SignedIn/CommuterDashboard'));
const StripeCustomConnectReceiver = lazy(() => import('views/SignedIn/BusinessDashboard/views/Settings/components/StripeConnect/StripeCustomConnectReceiver'));
const OauthStripeReceiver = lazy(() => import('views/SignedIn/BusinessDashboard/views/Settings/components/StripeConnect/StripeConnectOAuthReceiver'));
const NotFound = lazy(() => import('views/Anonymous/views/404'));
const Auth = lazy(() => import('views/Anonymous/views/Auth'));
const SelectDashboard = lazy(() => import('views/SignedIn/SelectDashboard'));
const InvoiceView = lazy(() => import('views/SignedIn/Invoice'));
const OAuthReceiver = lazy(() => import('shared/components/common/OAuthReceiver'));
const EmailVerification = lazy(() => import('views/Anonymous/views/EmailVerification'));
const Onboarding = lazy(() => import('views/SignedIn/CommuterDashboard/views/Onboarding'));
const Invitation = lazy(() => import('views/Anonymous/views/Invitation'));
const LOADER_ID = 'loader';
const enterpriseOrOrg = 'enterprises|organizations';
const renderLoading = () => <PageLoader />;
function Dashboards(t0) {
  const $ = _c(4);
  const {
    location
  } = t0;
  const {
    currentAccount,
    featureFlags
  } = useCurrentAccount();
  if (featureFlags && needsOnboardingInfo(currentAccount) && location.pathname !== CommuterRouting.onboarding.getRoute()) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = CommuterRouting.onboarding.getRoute();
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== location.pathname) {
      t2 = <Redirect to={{
        pathname: t1,
        state: {
          origin: location.pathname
        }
      }} />;
      $[1] = location.pathname;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Switch><Route path={CommuterRouting.basePage.superAdmin.path} component={UserDashboard} /><Route path={PublicRouting.oAuth.arc.path} component={BusinessDashboardProviders} /><Route path={PublicRouting.oAuth.stripe.customConnect.path} component={StripeCustomConnectReceiver} /><Route path={PublicRouting.oAuth.stripe.path} component={OauthStripeReceiver} /><Route path={AdminRouting.basePage.path} component={BusinessDashboardProviders} /><Route path={`${AdminRouting.root.path}/`} component={BusinessDashboardProviders} /><Route exact={true} path="/accounts/:accountId/card" render={_temp} /><Route path={CommuterRouting.basePage.path} component={UserDashboard} /><Route path="*" component={NotFound} /></Switch>;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

// Exported for tests: the redirect's resumePathname serialization is regression-locked.
function _temp() {
  return <Redirect to="/card" />;
}
export function PrivateRoutes(t0) {
  const $ = _c(15);
  const {
    match,
    location,
    logoutParams
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getAuthenticatedAccountId();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const accountToken = t1;
  const {
    cookieconsent
  } = useConfig();
  let t2;
  let t3;
  if ($[1] !== cookieconsent) {
    t2 = () => {
      if (!cookieconsent) {
        return;
      }
      cookieconsent?.hide();
    };
    t3 = [accountToken, cookieconsent];
    $[1] = cookieconsent;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  if (!accountToken && match.url !== PublicRouting.auth.path) {
    let t4;
    if ($[4] !== logoutParams) {
      t4 = logoutParams ? `${qs.stringify(logoutParams)}` : undefined;
      $[4] = logoutParams;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    const t5 = `${location.pathname}${location.search}${location.hash}`;
    let t6;
    if ($[6] !== t5) {
      t6 = {
        resumePathname: t5
      };
      $[6] = t5;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== t4 || $[9] !== t6) {
      t7 = <Redirect to={{
        pathname: PublicRouting.auth.path,
        search: t4,
        state: t6
      }} />;
      $[8] = t4;
      $[9] = t6;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  let t4;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SuperAdminAlert />;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ErrorBoundary ErrorComponent={DashboardErrorFallback} onError={handleDashboardError}>{t4}<Switch><Route exact={true} path={CommonRouting.select.path} component={SelectDashboard} /><Route exact={true} path={CommonRouting.invoice.detail.path} component={InvoiceView} /><Route path={CommuterRouting.onboarding.path} component={Onboarding} /><Route path="*" component={Dashboards} /></Switch></ErrorBoundary>;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== match.params.accountId) {
    t6 = <CurrentAccountProvider urlAccountId={match.params.accountId}>{t5}</CurrentAccountProvider>;
    $[13] = match.params.accountId;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
const PrivateRoutesContainer = connect((state: RootState, props: RouteComponentProps) => ({
  isAuthenticated: authSelectors.isLoggedIn(state),
  logoutParams: authSelectors.getLogoutParams(state),
  ...props
}), null)(PrivateRoutes);
const setApiHeaders = (locale: LocalesType) => {
  setLocaleHeader(locale);
};
function DashboardRoutes() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Switch><Route path={PublicRouting.auth.path} component={Auth} /><Route path={PublicRouting.invitation.path} component={Invitation} /><Route exact={true} path={PublicRouting.root.path} render={_temp2} /><Route exact={true} path={PublicRouting.oAuth.mobilityService.path} component={OAuthReceiver} /><Route exact={true} path={PublicRouting.emailVerification.path} component={EmailVerification} /><Route path={`/:entity(${enterpriseOrOrg})/:entityId(${uuidPattern})`} render={_temp3} /><Route exact={true} path={AdminRouting.root.path} render={_temp4} /><Route path={`${AdminRouting.root.path}/*`} component={PrivateRoutesContainer} /><Route path="/accounts/:accountId" component={PrivateRoutesContainer} /><Route path="*" component={PrivateRoutesContainer} /></Switch>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp4(t0) {
  const {
    match: match_0
  } = t0;
  return <Redirect to={`${match_0.url}/${AdminDashboardPages.Compliance}`} />;
}
function _temp3(t0) {
  const {
    match,
    location
  } = t0;
  return <Redirect to={{
    ...location,
    pathname: `${AdminRouting.root.getRoute(match.params.entityId)}${location.pathname.replace(match.url, "")}`
  }} />;
}
function _temp2() {
  return <Redirect to={PublicRouting.auth.path} />;
}
function AppRoutes() {
  const $ = _c(6);
  const isOnline = useOnlineStatus();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = renderLoading();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== isOnline) {
    t1 = !isOnline ? <Alert closable={true} style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      height: "70px"
    }} banner={true} title={<FormattedMessage id="global.offline.warning" />} className="u-elevation-3" /> : null;
    $[1] = isOnline;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Switch><Route exact={true} strict={true} path={PublicRouting.enterpriseRegistration.path} render={_temp5} /><Route path={`${PublicRouting.enterpriseRegistration.path}/`} component={EnterpriseRegistration} /><Route path={PublicRouting.enterpriseSurvey.publicWelcome.path} component={AnonymousLandingPage} /><Route exact={true} strict={true} path={PublicRouting.enterpriseSurvey.path} render={_temp6} /><Route path={`${PublicRouting.enterpriseSurvey.path}/`} component={SurveyLandingPage} /><Route path="*" component={DashboardRoutes} /></Switch>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <Suspense fallback={t0}>{t1}{t2}</Suspense>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp6(t0) {
  const {
    match: match_0
  } = t0;
  return <Redirect to={`${match_0.url}/`} />;
}
function _temp5(t0) {
  const {
    match
  } = t0;
  return <Redirect to={`${match.url}/`} />;
}
function App(t0) {
  const $ = _c(20);
  const {
    locale,
    isAuthenticated
  } = t0;
  const onError = useReactQueryOnError();
  let t1;
  let t2;
  if ($[0] !== onError) {
    t1 = () => {
      queryClient.getQueryCache().config.onError = onError;
    };
    t2 = [onError];
    $[0] = onError;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const {
    theme: t3
  } = useConfig();
  const {
    currentTheme
  } = t3;
  let t4;
  let t5;
  if ($[3] !== currentTheme) {
    t4 = () => {
      if (currentTheme === AppThemes.DARK) {
        document.body.classList.add("app-theme--dark");
      } else {
        if (currentTheme === AppThemes.LIGHT) {
          document.body.classList.remove("app-theme--dark");
        }
      }
    };
    t5 = [currentTheme];
    $[3] = currentTheme;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== locale) {
    t6 = () => setApiHeaders(locale || DEFAULT_LOCALE);
    t7 = [locale];
    $[6] = locale;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  useEffect(_temp9, t8);
  const isHydratingSso = useSsoLandingHydration();
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = getUrlSubdomain();
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const urlSubdomain = t9;
  const {
    data: subdomainData,
    isLoading: isLoadingSubdomain,
    error: subdomainError
  } = useSubdomain();
  let t10;
  if ($[11] !== subdomainData) {
    t10 = () => {
      if (!urlSubdomain && subdomainData) {
        const subdomainQuery = queryCache.find({
          queryKey: subdomainData.queryKey
        });
        if (subdomainQuery) {
          queryCache.remove(subdomainQuery);
        }
      }
    };
    $[11] = subdomainData;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== isAuthenticated || $[14] !== subdomainData) {
    t11 = [isAuthenticated, urlSubdomain, subdomainData];
    $[13] = isAuthenticated;
    $[14] = subdomainData;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  useEffect(t10, t11);
  let t12;
  if ($[16] !== isHydratingSso || $[17] !== isLoadingSubdomain || $[18] !== subdomainError) {
    t12 = subdomainError ? <GenericError type="page" error={subdomainError} title={<FormattedMessage id="error.load.account" />} buttonText={<FormattedMessage id="global.goBackToLogin" />} /> : isLoadingSubdomain || isHydratingSso ? <PageLoader /> : <div className="viewport"><AppRoutes /></div>;
    $[16] = isHydratingSso;
    $[17] = isLoadingSubdomain;
    $[18] = subdomainError;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
}
function _temp9() {
  const handleMouseDown = _temp7;
  const handleKeyDown = _temp8;
  document.body.addEventListener("mousedown", handleMouseDown);
  document.body.addEventListener("keydown", handleKeyDown);
  return () => {
    document.body.removeEventListener("mousedown", handleMouseDown);
    document.body.removeEventListener("keydown", handleKeyDown);
  };
}
function _temp8(event) {
  if (event.keyCode === 9) {
    document.body.classList.remove("using-mouse");
  }
}
function _temp7() {
  document.body.classList.add("using-mouse");
}
function TopLevelError(t0) {
  const $ = _c(5);
  const {
    error
  } = t0;
  let t1;
  let t2;
  if ($[0] !== error) {
    t1 = () => {
      if (isRecoverableLoadError(error)) {
        handleChunkLoadError();
      }
    };
    t2 = [error];
    $[0] = error;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const t3 = `Sorry for the inconvenience, please refresh and if it persists reach out to us and let us help you. ${error?.message}`;
  let t4;
  if ($[3] !== t3) {
    t4 = <GenericError title="Unexpected Error" description={t3} buttonText="Go Back Home" />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
class AppWithErrorBoundaries extends Component<AppWithErrorBoundariesProps & AppWithErrorBoundariesReduxProps> {
  componentDidMount() {
    this.removeLoadingNode();
  }
  handleError = (e: unknown) => {
    logger.captureException(e, {
      operation: 'app_root_error_handler'
    });
  };
  removeLoadingNode() {
    try {
      // App is mounted we don't need the page loader anymore.
      // It was set in the html to have a 'splash screen'
      const loadingNode = document.getElementById(LOADER_ID);
      if (loadingNode) {
        loadingNode.remove();
      }
    } catch {
      // Do nothing
    }
  }
  render() {
    const {
      store,
      persistor,
      locale,
      isAuthenticated
    } = this.props;
    return <ErrorBoundary onError={this.handleError} ErrorComponent={TopLevelError}>
        <CookiesProvider>
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <PersistGate loading={null} persistor={persistor}>
                <StyleProvider layer>
                  <ConfigProvider locale={locale}>
                    <OnlineStatusProvider>
                      <Router history={history}>
                        <MetricsLocalizer locale={locale}>
                          <App isAuthenticated={isAuthenticated} locale={locale} />
                        </MetricsLocalizer>
                      </Router>
                    </OnlineStatusProvider>
                    <CookieConsent />
                  </ConfigProvider>
                </StyleProvider>
              </PersistGate>
              <ReactQueryDevtools />
            </QueryClientProvider>
            {process.env.NODE_ENV === 'development' ? <DevTools /> : null}
          </Provider>
        </CookiesProvider>
      </ErrorBoundary>;
  }
}
interface AppWithErrorBoundariesProps {
  store: Store;
  persistor: Persistor;
}
interface AppWithErrorBoundariesReduxProps {
  locale?: LocalesType;
  isAuthenticated?: boolean;
}
const mapStateToProps = (state: RootState) => {
  const {
    locale
  } = state.localSettings;
  return {
    locale,
    isAuthenticated: authSelectors.isLoggedIn(state) as boolean | undefined,
    logoutParams: authSelectors.getLogoutParams(state) as Record<string, any> | undefined
  };
};
const ConnectedApp = connect<ReturnType<typeof mapStateToProps>, unknown, AppWithErrorBoundariesProps, RootState & PersistPartial>(mapStateToProps, null)(withProfiler(AppWithErrorBoundaries));
export default ConnectedApp;